import { Component, input, model } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { provideNativeDateAdapter } from '@angular/material/core';
import { DayAvailability, MeetingPreferences } from '../../../../../../models/meeting-preferences';
import {
  MAX_INTERVALS_PER_DAY,
  dayError,
} from '../../../../../../helpers/meeting-preferences-helpers';

/**
 * Presentational weekly-hours card. The container owns edit mode and the draft;
 * this component only reads `editing` and writes back through the `days` model.
 */
@Component({
  selector: 'app-counselor-availability',
  imports: [
    DatePipe,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatTimepickerModule,
    MatInputModule,
    MatTooltipModule,
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './counselor-availability.html',
  styleUrl: './counselor-availability.css',
})
export class CounselorAvailability {
  /** Two-way: the container holds this slice of the preferences draft. */
  readonly days = model.required<DayAvailability[]>();

  /** Driven by the container's single Edit / Save / Cancel control. */
  readonly editing = input<boolean>(false);

  /** Re-exported so the template can render each day's message. */
  readonly dayError = dayError;

  /** Exposed so the template can disable Add once a day is full. */
  readonly maxIntervalsPerDay = MAX_INTERVALS_PER_DAY;

  /** Lower bound for the time pickers, so the panel opens around business hours instead of midnight. */
  readonly earliestTime = MeetingPreferences.at(8, 0);

  /** Single-letter badge label, e.g. "M". Row order disambiguates Tue from Thu. */
  letter(day: string): string {
    return day.charAt(0);
  }

  /** Enable/disable a day. Enabling an empty day seeds a default interval. */
  toggleDay(dayOfWeek: number, enabled: boolean): void {
    this.days.update((days) =>
      days.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        const intervals =
          enabled && d.intervals.length === 0
            ? [MeetingPreferences.defaultInterval()]
            : d.intervals;
        return { ...d, enabled, intervals };
      }),
    );
  }

  /**
   * Append another interval to a day, starting where the previous one ended.
   * Caps at `MAX_INTERVALS_PER_DAY` so a full day can't be pushed into an
   * invalid state — the button is disabled there too, this is the backstop.
   */
  addInterval(dayOfWeek: number): void {
    this.days.update((days) =>
      days.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        if (d.intervals.length >= MAX_INTERVALS_PER_DAY) return d;
        const last = d.intervals[d.intervals.length - 1];
        return { ...d, intervals: [...d.intervals, MeetingPreferences.nextInterval(last)] };
      }),
    );
  }

  /** Remove the interval at `index`; a day with no intervals falls back to disabled. */
  removeInterval(dayOfWeek: number, index: number): void {
    this.days.update((days) =>
      days.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        const intervals = d.intervals.filter((_, i) => i !== index);
        return { ...d, intervals, enabled: intervals.length > 0 && d.enabled };
      }),
    );
  }

  /**
   * Writes a time back through the signal rather than mutating the interval in
   * place.
   */
  setTime(dayOfWeek: number, index: number, field: 'start' | 'end', value: Date | null): void {
    this.days.update((days) =>
      days.map((d) => {
        if (d.dayOfWeek !== dayOfWeek) return d;
        return {
          ...d,
          intervals: d.intervals.map((interval, i) =>
            i === index ? { ...interval, [field]: value } : interval,
          ),
        };
      }),
    );
  }
}
